import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { AccountTree as AccountTreeIcon } from '@mui/icons-material';
import { forecastHierarchyService } from '../../services/forecastHierarchy.service';

const LevelNavigation = ({ 
  runId, 
  hierarchyId, 
  hierarchyName,
  levels = [],
  onNavigateToNode,
  onNavigateToLevel 
}) => {
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [nodesAtLevel, setNodesAtLevel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Ensure levels is always an array
  const safeLevels = Array.isArray(levels) ? levels : [];
  
  // Debug logging
  console.log('LevelNavigation received levels:', levels);
  console.log('LevelNavigation safeLevels:', safeLevels);
  console.log('LevelNavigation selectedLevel:', selectedLevel);
  console.log('LevelNavigation hierarchyId:', hierarchyId);
  console.log('LevelNavigation runId:', runId);

  useEffect(() => {
    if (safeLevels.length > 0 && !selectedLevel) {
      // Start from the top level (level_order = 1) instead of the first level in the array
      const topLevel = safeLevels.find(level => level.level_order === 1) || safeLevels[0];
      console.log('Setting selectedLevel to:', topLevel);
      setSelectedLevel(topLevel);
    }
  }, [safeLevels, selectedLevel]);

  useEffect(() => {
    console.log('LevelNavigation: useEffect for fetchNodesAtLevel triggered');
    console.log('LevelNavigation: useEffect dependencies:', { selectedLevel, hierarchyId, runId });
    
    const fetchNodesAtLevel = async () => {
      console.log('fetchNodesAtLevel called with:', { selectedLevel, hierarchyId, runId });
      
      if (!selectedLevel || !hierarchyId) {
        console.log('Skipping fetchNodesAtLevel - missing:', { 
          hasSelectedLevel: !!selectedLevel, 
          hasHierarchyId: !!hierarchyId 
        });
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching nodes for level:', selectedLevel);
        console.log('Hierarchy ID:', hierarchyId);
        console.log('Level ID:', selectedLevel.level_id);

        const response = await forecastHierarchyService.getNodesAtLevel(
          hierarchyId, 
          selectedLevel.level_id,
          {
            run_id: runId,
            non_empty_only: true,
            limit: 100
          }
        );

        console.log('Nodes API response:', response);
        console.log('Response status:', response.status);
        console.log('Response data:', response.data);
        console.log('Response data.nodes:', response.data?.nodes);

        if (response.status === 'success') {
          // Fix: The API returns data.nodes, not just data
          const nodes = response.data?.nodes || [];
          console.log('Setting nodes:', nodes);
          console.log('Nodes array length:', nodes.length);
          setNodesAtLevel(nodes);
        } else {
          throw new Error(response.message || 'Failed to fetch nodes');
        }
      } catch (err) {
        console.error('Error fetching nodes at level:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNodesAtLevel();
  }, [selectedLevel, hierarchyId, runId]);

  const handleLevelChange = (event) => {
    const levelId = event.target.value;
    const level = safeLevels.find(l => l.level_id === levelId);
    setSelectedLevel(level);
    if (onNavigateToLevel) {
      onNavigateToLevel(level);
    }
  };

  const handleNodeClick = (node) => {
    if (onNavigateToNode) {
      onNavigateToNode(node);
    } else {
      // Navigate to standalone node details page
      navigate(`/forecast-hierarchy/${runId}/hierarchy/${hierarchyId}/node/${node.node_id}`);
    }
  };

  // Show loading state if no levels are available
  if (safeLevels.length === 0) {
    return (
      <Box>
        <Card>
          <CardHeader
            title="Navigate by Level"
            subheader={`Hierarchy: ${hierarchyName}`}
          />
          <CardContent>
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading hierarchy levels...</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Level Selector */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Navigate by Level"
          subheader={`Hierarchy: ${hierarchyName}`}
        />
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Select Level</InputLabel>
            <Select
              value={selectedLevel?.level_id || ''}
              onChange={handleLevelChange}
              label="Select Level"
            >
              {safeLevels.map((level) => (
                <MenuItem key={level.level_id} value={level.level_id}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {level.display_name || level.level_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Level {level.level_order} • {level.is_leaf_level ? 'Leaf' : 'Parent'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Nodes at Selected Level */}
      <Card>
        <CardHeader
          title={`Nodes at ${selectedLevel?.display_name || selectedLevel?.level_name || 'Level'}`}
          subheader={loading ? 'Loading...' : `${nodesAtLevel.length} nodes found`}
          avatar={<AccountTreeIcon />}
        />
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">
              {error}
            </Alert>
          ) : nodesAtLevel.length > 0 ? (
            <List>
              {nodesAtLevel.map((node, index) => (
                <React.Fragment key={node.node_id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleNodeClick(node)}>
                      <ListItemText
                        primary={node.node_name}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              ID: {node.node_id} • Key: {node.node_key}
                            </Typography>
                            <Typography variant="caption" display="block">
                              SKUs: {node.sku_count} • Stores: {node.store_count}
                            </Typography>
                            {node.node_path && (
                              <Typography variant="caption" display="block">
                                Path: {node.node_path}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Chip 
                        label="View" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < nodesAtLevel.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body2" color="text.secondary">
                No nodes found at this level.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LevelNavigation; 